import {Button, Modal, Paper, PasswordInput, Select, Table, TextInput, Title} from '@mantine/core';
import useTranslation from 'src/i18next.config';
import {useUserService} from 'src/services';
import {useEffect, useState} from 'react';
import {CanDeleteUserResponseType} from 'src/types';
import {User} from 'src/types/models/User';
import {showNotification} from '@mantine/notifications';
import {useNavigate} from 'react-router-dom';
import {ERoutes} from 'src/types/enum/routes.enum';
import {userProfileActions} from 'src/store/reducers/user-reducer';
import {ERoles} from 'src/types/enum/roles.enum';
import ConfirmationModal from '../../core/ConfirmationModals';
import {useOrganizationService} from 'src/services/organization';
import {Organization} from 'src/types/models/Organization';

export function UsersList() {
    const {t} = useTranslation();
    const userService = useUserService();
    const organizationService = useOrganizationService();

    const [errorMessage] = useState<string | null>();
    const [users, setUsers] = useState<User[]>();
    const [userToEdit, setUserToEdit] = useState<User>();
    const [input, setInput] = useState('');
    const userProfile = userProfileActions.useUserProfile();
    const navigate = useNavigate();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState(t('areYouSure') as string);
    const [selectedUserId, setSelectedUserId] = useState('');

    const [searchText, setSearchText] = useState('');
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

    useEffect(() => {
        if (userProfile.isAdmin) {
            organizationService.getOrganizations({
                error: () => { /* no-op */ },
                success: res => {
                    setOrganizations(res);
                },
            });
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [selectedOrgId]);

    function fetchUsers() {
        if (selectedOrgId) {
            userService.getUsersByOrganization(
                {
                    error: error => {
                        console.error(error);
                    },
                    success: res => {
                        return res.data;
                    },
                },
                selectedOrgId
            ).then((usersList: User[]) => {
                setUsers(usersList);
            });
        } else {
            userService
                .getUsers({
                    error: error => {
                        console.error(error);
                    },
                    success: res => {
                        return res.data;
                    },
                })
                .then((usersList: User[]) => {
                    setUsers(usersList);
                });
        }
    }

    const usersArray = users ? users : [];

    const filteredUsers = usersArray.filter(user => {
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        return (
            user.firstName?.toLowerCase().includes(search) ||
            user.lastName?.toLowerCase().includes(search) ||
            user.email?.toLowerCase().includes(search)
        );
    });

    const rows = filteredUsers.map((user: User) => (
        <Table.Tr key={user._id}>
            <Table.Td>{user.firstName}</Table.Td>
            <Table.Td>{user.lastName}</Table.Td>
            <Table.Td>{user.email}</Table.Td>
            <Table.Td>{user.organization?.name}</Table.Td>
            <Table.Td>{t(`roles.${user.role}`)}</Table.Td>
            <Table.Td>{user.description}</Table.Td>
            <Table.Td>
                <Button
                    disabled={user.role === ERoles.ADMIN && !userProfile.isAdmin}
                    color="cyan.8"
                    variant="outline"
                    onClick={() => {
                        setUserToEdit(user);
                        setIsPasswordModalOpen(true);
                    }}
                >
                    {t('user.editUserPasswordBtn')}
                </Button>
            </Table.Td>
            <Table.Td>
                <Button
                    disabled={user.role === ERoles.ADMIN && !userProfile.isAdmin}
                    variant="outline"
                    onClick={() => {
                        navigate(ERoutes.UPDATE_USER, {
                            state: {userToUpdate: user},
                        });
                    }}
                >
                    {t('user.editUser')}
                </Button>
            </Table.Td>
            <Table.Td>
                <Button
                    disabled={user.role === ERoles.ADMIN && !userProfile.isAdmin}
                    color="red"
                    variant="outline"
                    onClick={() => {
                        setSelectedUserId(user._id);

                        userService.getCanDeleteUser(
                            {
                                error: error => {
                                    showNotification({
                                        title: t('user.deleteUserNotification'),
                                        message: error ? error.message : t('user.deleteUserErrorMessage'),
                                        color: 'red',
                                    });
                                },
                                success: (canDeleteUserResponse: CanDeleteUserResponseType) => {
                                    if (!canDeleteUserResponse.canDelete) {
                                        setModalMessage(canDeleteUserResponse.message);
                                    }
                                    setIsModalOpen(true);
                                },
                            },
                            user._id
                        );
                    }}
                >
                    {t('user.deleteUserBtn')}
                </Button>
            </Table.Td>
        </Table.Tr>
    ));

    function changePassword() {
        setIsPasswordModalOpen(false);
        userService.updateUserPassword(
            {
                error: err => {
                    showNotification({
                        title: t('user.editUserPasswordNotification'),
                        message: err.message ? err.message : t('user.editUserPasswordErrorMessage'),
                        color: 'red',
                    });
                },
                success: () => {
                    showNotification({
                        title: t('user.editUserPasswordNotification'),
                        message: t('user.editUserPasswordSuccessMessage'),
                        color: 'green',
                    });
                },
            },
            userToEdit?._id || '',
            {password: input}
        );
        setUserToEdit(undefined);
        setInput('');
    }

    function deleteUser(id: string) {
        userService.deleteUser(
            {
                error: error => {
                    showNotification({
                        title: t('user.deleteUserNotification'),
                        message: error ? error.message : t('user.deleteUserErrorMessage'),
                        color: 'red',
                    });
                },
                success: () => {
                    showNotification({
                        title: t('user.deleteUserNotification'),
                        message: t('user.deleteUserSuccessMessage'),
                        color: 'green',
                    });
                    setUsers(users?.filter(user => user._id != id));
                },
            },
            id
        );
    }

    function onModalClose() {
        setIsModalOpen(false);
    }

    function onModalConfirmation() {
        setIsModalOpen(false);
        deleteUser(selectedUserId);
    }

    return (
        <>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end', marginBottom: 20}}>
                <TextInput
                    placeholder={t('user.searchPlaceholder') as string}
                    value={searchText}
                    onChange={event => setSearchText(event.target.value)}
                    style={{minWidth: 200}}
                />
                {userProfile.isAdmin && (
                    <Select
                        placeholder={t('organization.label') as string}
                        data={organizations.map(org => ({value: org._id, label: org.name}))}
                        value={selectedOrgId}
                        onChange={setSelectedOrgId}
                        searchable
                        clearable
                        style={{minWidth: 200}}
                    />
                )}
                <Button
                    color="cyan.8"
                    type="submit"
                    onClick={() => {
                        navigate(ERoutes.ADD_USER);
                    }}
                >
                    {t('user.addUserBtn')}
                </Button>
            </div>
            <Paper shadow="md" style={{width: 'fit-content', margin: 'auto'}}>
                <Table.ScrollContainer minWidth={700}>
                    <Table highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{minWidth: '100px'}}>{t('firstName')}</Table.Th>
                                <Table.Th style={{minWidth: '100px'}}>{t('lastName')}</Table.Th>
                                <Table.Th>{t('email')}</Table.Th>
                                <Table.Th>{t('organization.label')}</Table.Th>
                                <Table.Th style={{minWidth: '140px'}}>{t('role')}</Table.Th>
                                <Table.Th style={{minWidth: '10px'}}>{t('description')}</Table.Th>
                                <Table.Th>{}</Table.Th>
                                <Table.Th>{}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>{rows}</Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>
            <Modal
                opened={isPasswordModalOpen}
                title={`${t('user.editUserPasswordBtn')} - ${userToEdit?.firstName} ${userToEdit?.lastName}`}
                onClose={() => {setIsPasswordModalOpen(false)}}>
                    <div
                        style={{
                            width: 300,
                            height: 'auto',
                            padding: 20,
                            alignItems: 'center',
                        }}
                    >
                        <Title order={3}>{t('enterNewPassword')}</Title>

                        <PasswordInput
                            id="password"
                            placeholder={t('newPassword').toString()}
                            onChange={event => setInput(event.target.value)}
                            value={input}
                            onKeyDown={event => {
                                if (event.key === 'Enter') {
                                    changePassword();
                                }
                            }}
                        />
                        <Button style={{marginTop: 20}} color="cyan.8" type="submit" onClick={changePassword}>
                            {t('update')}
                        </Button>
                    </div>
            </Modal>
            <ConfirmationModal
                isOpen={isModalOpen}
                title={t('user.deleteUser')}
                text={modalMessage}
                onClose={onModalClose}
                onConfirmation={onModalConfirmation}
            ></ConfirmationModal>
        </>
    );
}
